import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router";
import { Trans, useLingui } from "@lingui/react/macro";
import { Heading } from "@medoma/components/Heading/Heading";
import { FilledButton } from "@medoma/components/Button/Button";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import InputField from "@medoma/components/InputField/InputField";
import Select from "@medoma/components/Select/Select";
import Form from "@/components/Form/Form";
import { CloseButton } from "@/components/NavigationButtons/NavigationButtons";
import { categoriesQueryOptions, createAsset } from "@/api/Equipment";
import { deducedError } from "@/Utils/ErrorUtils";
import styles from "./CreateAssetForm.module.scss";

type CreateAssetFormFields = {
  name: string;
  categoryId: string;
};

type CreateAssetFormContentProps = {
  defaultValues: CreateAssetFormFields;
  categories: { id: string; name: string }[];
};

const CreateAssetFormContent = ({
  defaultValues,
  categories,
}: CreateAssetFormContentProps) => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setError,
    setFocus,
    reset,
    watch,
    formState: { errors },
  } = useForm<CreateAssetFormFields>({
    defaultValues,
  });

  // oxlint-disable-next-line react/incompatible-library -- React Hook Form's watch API is not compiler-memoizable.
  const selectedCategoryId = watch("categoryId");
  const selectedCategoryName = categories.find(
    (c) => c.id === selectedCategoryId,
  )?.name;

  const { mutate: createAssetMutation, isPending } = useMutation({
    mutationFn: ({ name, categoryId }: CreateAssetFormFields) =>
      createAsset({ name, categoryId }),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries();
      // Stay on the form so user can add another asset to the same category.
      reset({ name: "", categoryId: variables.categoryId });
      setFocus("name");
    },
    onError: (error) => {
      setError("root.server", {
        message: `${t`Kunde inte skapa utrustning.`} ${deducedError(error)}`,
      });
    },
  });

  const validateAndSubmit = handleSubmit((values) => {
    createAssetMutation({
      name: values.name.trim(),
      categoryId: values.categoryId,
    });
  });

  return (
    <>
      <header className={styles.header}>
        <Heading level="h1">
          {selectedCategoryName ? (
            <Trans>Lägg till utrustning i {selectedCategoryName}</Trans>
          ) : (
            <Trans>Lägg till utrustning</Trans>
          )}
        </Heading>
        <CloseButton close={() => navigate("..")} />
      </header>
      <Form onSubmit={validateAndSubmit}>
        <Select
          label={t`Kategori`}
          errorMessage={errors.categoryId?.message}
          {...register("categoryId", {
            required: { value: true, message: t`Välj en kategori` },
          })}
        >
          <option value="" disabled>
            {t`Välj kategori`}
          </option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </Select>

        <InputField
          label={t`Namn`}
          showOptionalLabel={false}
          autoComplete="off"
          errorMessage={errors.name?.message}
          {...register("name", {
            required: { value: true, message: t`Ange ett namn` },
          })}
        />

        {errors.root?.server?.message ? (
          <ErrorMessage message={errors.root.server.message} />
        ) : null}

        {isPending ? (
          <Loading message={t`Lägger till utrustning`} />
        ) : (
          <div className={styles.actions}>
            <FilledButton type="submit" disabled={isPending}>
              <Trans>Lägg till</Trans>
            </FilledButton>
          </div>
        )}
      </Form>
    </>
  );
};

export const CreateAssetForm = () => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedCategoryId = searchParams.get("categoryId");

  const {
    data: categories,
    isPending,
    isError,
    error,
  } = useQuery(categoriesQueryOptions());

  if (isPending) {
    return <Loading message={t`Hämtar kategorier`} />;
  }

  if (isError) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta kategorier.`} ${deducedError(error)}`}
      />
    );
  }

  if (categories.length === 0) {
    return (
      <>
        <header className={styles.header}>
          <Heading level="h1">
            <Trans>Lägg till utrustning</Trans>
          </Heading>
          <CloseButton close={() => navigate("..")} />
        </header>
        <ErrorMessage
          message={t`Du behöver skapa minst en kategori innan du kan lägga till utrustning.`}
        />
      </>
    );
  }

  const preselectedCategory = preselectedCategoryId
    ? categories.find((c) => c.id === preselectedCategoryId)
    : undefined;

  const initialCategoryId =
    preselectedCategory?.id ??
    (categories.length === 1 ? (categories[0]?.id ?? "") : "");

  return (
    <CreateAssetFormContent
      key={`new-${initialCategoryId}`}
      defaultValues={{ name: "", categoryId: initialCategoryId }}
      categories={categories}
    />
  );
};
